import {
  Button,
  ButtonGroup,
  Flex,
  Link,
  Select,
  Spinner,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  ProjectOverviewScope,
  useGetProjectOverviewQuery,
  type ProjectOverviewDto,
  type ProjectOverviewRowDto,
} from "../../autogen/bff-api";
import { FilterChip } from "../../common/components/FilterChip";
import { useApiError } from "../../common/errors/useApiError";
import { formatNumber } from "../../common/formatting/formatNumber";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { HorizontalScrollBox } from "../../common/HorizontalScrollBox";
import { ListLoadError } from "../../common/ListLoadError";
import { displayPersonName } from "../../common/persons/displayPersonName";
import { urls } from "../../urls";
import { contractValueFigure } from "../projects/view-single/ProcurementSummary";
import { useProjectTypes } from "../projects/useProjectTypes";
import { OverviewFigureGrid, OverviewFigureTile } from "./OverviewFigureTile";
import { ProjectValueCharts } from "./ProjectValueCharts";
import {
  downloadProjectOverview,
  figureFromAddress,
  isBehindFigure,
  NOT_SHOWN,
  periodText,
  PROJECT_FIGURES,
  projectFigureDefinition,
  projectFigureLabel,
  projectFigureLink,
  projectFigureValue,
  projectName,
  SCOPES,
  scopeFromAddress,
  scopeInAddress,
  scopeLabel,
  SHOW_PARAM,
  STATUS_PARAM,
  TYPE_PARAM,
  type ProjectFigure,
} from "./projectFigures";

export const ProjectsOverview = () => {
  const { t } = useTranslation();
  const { message } = useApiError();
  const displayDate = useDisplayDate();
  const projectTypes = useProjectTypes();
  const [params, setParams] = useSearchParams();
  const scope = scopeFromAddress(params.get(STATUS_PARAM));
  const projectTypeId = projectTypes.find((type) => type.value === params.get(TYPE_PARAM))?.value;
  const shown = figureFromAddress(params.get(SHOW_PARAM));
  const { data, isLoading, isFetching, error, refetch } = useGetProjectOverviewQuery({ scope, projectTypeId });

  const change = (key: string, value: string | null) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (value) updated.set(key, value);
        else updated.delete(key);
        return updated;
      },
      { replace: true },
    );

  const rows = data ? data.projects.filter((row) => !shown || isBehindFigure(shown, row)) : [];
  const narrowedLine = useRef<HTMLDivElement>(null);
  const shownBefore = useRef(shown);
  useEffect(() => {
    if (shown && shown !== shownBefore.current) {
      narrowedLine.current?.scrollIntoView({ block: "start", behavior: "smooth" });
      narrowedLine.current?.focus({ preventScroll: true });
    }
    shownBefore.current = shown;
  }, [shown]);

  return (
    <Stack spacing="5">
      <Flex gap="3" wrap="wrap" align="center">
        <ButtonGroup size="sm" spacing="2" flexWrap="wrap" rowGap="2">
          {SCOPES.map((choice) => (
            <FilterChip
              key={choice}
              active={scope === choice}
              label={scopeLabel(t, choice)}
              onClick={() => change(STATUS_PARAM, scopeInAddress(choice))}
            />
          ))}
        </ButtonGroup>
        <Select
          size="sm"
          maxW="56"
          aria-label={String(t("Project type"))}
          value={projectTypeId ?? ""}
          onChange={(event) => change(TYPE_PARAM, event.target.value || null)}
        >
          <option value="">{t("All types")}</option>
          {projectTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </Select>
        {isFetching && !isLoading && <Spinner size="sm" color="smPrimary" />}
      </Flex>
      {isLoading ? (
        <Spinner color="smPrimary" />
      ) : error || !data ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : data.projects.length === 0 && scope === ProjectOverviewScope.All && !projectTypeId && !shown ? (
        <NoProjects />
      ) : (
        <>
          <Figures overview={data} params={params} />
          <ProjectValueCharts overview={data} />
          <Stack spacing="3">
            <Flex gap="3" wrap="wrap" align="center" justify="space-between">
              <Text as="h2" fontWeight="semibold" color="smText">
                {t("The projects")}
              </Text>
              <Button
                size="sm"
                variant="outline"
                isDisabled={rows.length === 0}
                onClick={() => downloadProjectOverview(t, rows, { retrieved: displayDate(new Date().toISOString()) }, displayDate)}
              >
                {t("Download")}
              </Button>
            </Flex>
            {shown && (
              <Flex
                ref={narrowedLine}
                tabIndex={-1}
                scrollMarginTop="4"
                _focus={{ outline: "none" }}
                _focusVisible={{ boxShadow: "outline" }}
                gap="2"
                align="center"
                wrap="wrap"
                data-testid="projects-narrowed"
              >
                <Text fontSize="sm" color="smMuted">
                  {t("Showing the projects behind «{{figure}}».", { figure: projectFigureLabel(t, shown) })}
                </Text>
                <Button size="sm" variant="link" colorScheme="teal" onClick={() => change(SHOW_PARAM, null)}>
                  {t("Show all projects")}
                </Button>
              </Flex>
            )}
            {rows.length === 0 ? (
              <Text color="smMuted">{t("No projects match the choice.")}</Text>
            ) : (
              <ProjectTable rows={rows} />
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};

const NoProjects = () => {
  const { t } = useTranslation();
  return (
    <Flex direction="column" align="center" gap="3" py="10" textAlign="center" data-testid="no-projects">
      <Text color="smText" fontWeight="semibold">
        {t("No ongoing or upcoming projects.")}
      </Text>
      <Text color="smMuted">{t("Projects created in the project list show up here.")}</Text>
      <Button as={RouterLink} to={urls.app.projects.viewAll} size="sm" variant="outline">
        {t("Go to projects")}
      </Button>
    </Flex>
  );
};

const Figures = ({ overview, params }: { overview: ProjectOverviewDto; params: URLSearchParams }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const { figures } = overview;
  const contractValue = figures.contractValue;
  return (
    <Stack spacing="4">
      <OverviewFigureGrid columns={{ base: 1, sm: 2, lg: 3 }}>
        {PROJECT_FIGURES.map((figure) => (
          <OverviewFigureTile
            key={figure}
            label={projectFigureLabel(t, figure)}
            shown={projectFigureValue(figure, overview)}
            below={
              figure === "startingSoon" && figures.startingSoonWithNothingUnderway > 0 ? (
                <Text fontSize="xs" color="smError">
                  {t("{{count}} have neither a contract nor a procurement", {
                    count: figures.startingSoonWithNothingUnderway,
                  })}
                </Text>
              ) : undefined
            }
            definition={projectFigureDefinition(t, figure, displayDate(overview.startsSoonUntil))}
            details={<Text>{figureDetails(t, figure, overview)}</Text>}
            link={projectFigureLink(params, figure)}
            testId={figure}
          />
        ))}
      </OverviewFigureGrid>
      <Stack spacing="1" fontSize="sm" color="smMuted">
        <Text>{t("Based on what you have access to. Projects that have ended are not included.")}</Text>
        {contractValue && contractValue.withoutTotalPrice > 0 && (
          <Text>
            {t("{{missing}} of {{total}} contracts in the projects have no total price and are not included in «Total contract value».", {
              missing: formatNumber(contractValue.withoutTotalPrice),
              total: formatNumber(contractValue.contracts),
            })}
          </Text>
        )}
        {contractValue?.drafts ? (
          <Text>
            {t("{{count}} draft contracts in the projects are not included in «Total contract value».", {
              count: contractValue.drafts,
            })}
          </Text>
        ) : null}
      </Stack>
    </Stack>
  );
};

const figureDetails = (t: TFunction, figure: ProjectFigure, overview: ProjectOverviewDto): string => {
  const { figures } = overview;
  const projectsBehind = overview.projects.filter((row) => isBehindFigure(figure, row)).length;
  switch (figure) {
    case "startingSoon":
      return t("{{count}} projects, {{bare}} of them with neither a contract nor a procurement.", {
        count: figures.startingSoon,
        bare: figures.startingSoonWithNothingUnderway,
      });
    case "contractValue":
      return figures.contractValue
        ? t("{{count}} contracts in {{projects}} projects.", {
            count: figures.contractValue.contracts,
            projects: projectsBehind,
          })
        : t("You do not have access to the contracts.");
    case "endingBeforeProjectEnd":
      return figures.contractsEndingBeforeProjectEnd == null
        ? t("You do not have access to the contracts.")
        : t("{{count}} contracts in {{projects}} projects.", {
            count: figures.contractsEndingBeforeProjectEnd,
            projects: projectsBehind,
          });
    case "productsWithoutProcurement":
      return t("{{count}} of {{total}} products have a procurement", {
        count: figures.products - figures.productsWithoutProcurement,
        total: figures.products,
      });
    default:
      return t("{{count}} projects", { count: projectsBehind });
  }
};

const ProjectTable = ({ rows }: { rows: ProjectOverviewRowDto[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  return (
    <HorizontalScrollBox bg="smBackground" border="1px solid" borderColor="smBorder" rounded="lg">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>{t("Project number")}</Th>
            <Th minW="48">{t("Project")}</Th>
            <Th>{t("Type")}</Th>
            <Th>{t("Period")}</Th>
            <Th>{t("Sourcing responsible")}</Th>
            <Th isNumeric>{t("Contracts")}</Th>
            <Th isNumeric>{t("Ongoing procurements")}</Th>
            <Th>{t("Products")}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr
              key={row.id}
              data-testid={`project-${row.id}`}
              onClick={row.visible ? () => navigate(urls.app.projects.view.go(row.id)) : undefined}
              _hover={row.visible ? { bg: "smBackgroundSecondary", cursor: "pointer" } : undefined}
            >
              <Td whiteSpace="nowrap">{row.externalId ?? ""}</Td>
              <Td>
                <Stack spacing="1" align="flex-start">
                  {row.visible ? (
                    <Link
                      as={RouterLink}
                      to={urls.app.projects.view.go(row.id)}
                      fontWeight="semibold"
                      color="smText"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {projectName(t, row)}
                    </Link>
                  ) : (
                    <Text color="smMuted" fontStyle="italic">
                      {projectName(t, row)}
                    </Text>
                  )}
                  {row.startsSoonWithoutSourcingResponsible && (
                    <Tag size="sm" colorScheme="orange" data-testid="starts-soon-without-owner">
                      {t("Starts soon without a sourcing responsible")}
                    </Tag>
                  )}
                </Stack>
              </Td>
              <Td>{row.projectType ? t(row.projectType) : ""}</Td>
              <Td whiteSpace="nowrap">{periodText(row, displayDate)}</Td>
              <Td>
                {row.sourcingResponsible ? (
                  <Flex gap="2" align="center" wrap="wrap">
                    <Text>{displayPersonName(row.sourcingResponsible)}</Text>
                    {row.withoutSourcingResponsible && (
                      <Tag size="sm" colorScheme="gray">
                        {t("Deactivated")}
                      </Tag>
                    )}
                  </Flex>
                ) : row.withoutSourcingResponsible ? (
                  <Text color="smMuted">{t("Sourcing responsible not provided")}</Text>
                ) : null}
              </Td>
              <Td isNumeric>
                {row.contracts ? (
                  <Stack spacing="0" align="flex-end">
                    <Text>{formatNumber(row.contracts.contracts)}</Text>
                    {row.contracts.contracts > 0 && (
                      <Text fontSize="xs" color="smMuted" whiteSpace="nowrap">
                        {contractValueFigure(row.contracts)}
                      </Text>
                    )}
                  </Stack>
                ) : (
                  NOT_SHOWN
                )}
              </Td>
              <Td isNumeric>{row.ongoingProcurements ?? NOT_SHOWN}</Td>
              <Td>
                {t("{{count}} of {{total}} products have a procurement", {
                  count: row.products.withProcurement,
                  total: row.products.total,
                })}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </HorizontalScrollBox>
  );
};
